import { api } from "./client";
import type { ApiResponse } from "./types";

export type OpportunityStatus = "open" | "filled" | "closed" | "cancelled";
export type SignupStatus =
  | "applied"
  | "accepted"
  | "rejected"
  | "completed"
  | "withdrawn";

export interface VolunteerOpportunity {
  id: string;
  institutionId: string;
  title: string;
  description: string;
  category: string;
  spotsAvailable: number;
  spotsFilled: number;
  yashPoints: number | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  status: OpportunityStatus;
  createdAt: string;
}

export interface VolunteerSignup {
  id: string;
  opportunityId: string;
  userId: string;
  status: SignupStatus;
  message: string | null;
  rejectionReason: string | null;
  createdAt: string;
  opportunity?: {
    id: string;
    title: string;
    category: string;
    date: string | null;
    institutionId: string;
    institution: { id: string; name: string; slug: string; avatarUrl: string | null };
  };
}

export function listOpportunitiesForInstitution(
  institutionId: string,
  params?: { status?: OpportunityStatus; upcomingOnly?: boolean; page?: number; limit?: number },
) {
  return api.get<ApiResponse<{ items: VolunteerOpportunity[]; meta: { total: number } }>>(
    `/institutions/${institutionId}/volunteer-opportunities`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function signupForOpportunity(
  opportunityId: string,
  body?: { message?: string },
) {
  return api.post<ApiResponse<VolunteerSignup>>(
    `/volunteer-opportunities/${opportunityId}/signup`,
    body ?? {},
  );
}

export function withdrawSignup(opportunityId: string) {
  return api.delete<ApiResponse<void>>(
    `/volunteer-opportunities/${opportunityId}/signup`,
  );
}

export function getMyVolunteerHistory(params?: {
  status?: SignupStatus;
  page?: number;
  limit?: number;
}) {
  return api.get<
    ApiResponse<{ items: VolunteerSignup[]; meta: { total: number } }>
  >("/users/me/volunteer-history", params as Record<string, string | number | boolean | undefined>);
}

// ─── Institution-admin review flow ─────────────────────────────────

export interface OpportunitySignup {
  id: string;
  status: string;
  message: string | null;
  rejectionReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  user: { id: string; name: string; avatarUrl: string | null };
}

export function listSignups(opportunityId: string) {
  return api.get<ApiResponse<{ signups: OpportunitySignup[] }>>(
    `/volunteer-opportunities/${opportunityId}/signups`,
  );
}

export function acceptSignup(opportunityId: string, signupId: string) {
  return api.post<ApiResponse<unknown>>(
    `/volunteer-opportunities/${opportunityId}/signups/${signupId}/accept`,
    {},
  );
}

export function rejectSignup(opportunityId: string, signupId: string, reason?: string) {
  return api.post<ApiResponse<unknown>>(
    `/volunteer-opportunities/${opportunityId}/signups/${signupId}/reject`,
    { reason },
  );
}

export function completeSignup(opportunityId: string, signupId: string) {
  return api.post<ApiResponse<unknown>>(
    `/volunteer-opportunities/${opportunityId}/signups/${signupId}/complete`,
    {},
  );
}
